import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import useSearch from "../../../hooks/useSearch";
import useWindowSize from "../../../utils/useWindowSize";
import { useSearchContext } from "./SearchProvider";
import SmallSearchBar from "./variants/SmallSearchBar";
import TinySearchBar from "./variants/TinySearchBar";

// Only the homepage hero renders this variant; loading it on demand keeps its
// date pickers out of the shared layout bundle. It is still server-rendered.
const NormalSearchBar = dynamic(() => import("./variants/NormalSearchBar"));

type SearchBarProps = {
  variant?: "tiny" | "small" | "normal" | "responsive";
  countryId?: string;
  locationId?: string;
  tags?: string[];
  reference?: string;
};

export default function SearchBar({
  variant = "responsive",
  reference,
  ...props
}: SearchBarProps) {
  const router = useRouter();

  const { width } = useWindowSize();

  const searchContext = useSearchContext();

  const [locationId, setLocationId] = useOptionalState(props.locationId, [
    searchContext.locationId,
    searchContext.setLocationId,
  ]);
  const [countryId, setCountryId] = useOptionalState(props.countryId, [
    searchContext.countryId,
    searchContext.setCountryId,
  ]);

  const [tags, setTags] = useOptionalState(props.tags && new Set(props.tags), [
    searchContext.tags,
    searchContext.setTags,
  ]);

  const { search, loading } = useSearch();

  const performSearch = async () => {
    search({
      locationId,
      tags,
      ref: reference,
    });
  };

  const searchBarProps = {
    ...searchContext,
    locationId,
    setLocationId,
    countryId,
    setCountryId,
    tags,
    setTags,
    loading,
    performSearch,
  };

  const calculatedVariant = useMemo(
    () =>
      (variant === "responsive"
        ? width <= 320
          ? "tiny"
          : width <= 640
          ? "small"
          : "normal"
        : variant) ?? "normal",
    [variant, width]
  );

  const Variant = {
    tiny: TinySearchBar,
    small: SmallSearchBar,
    normal: NormalSearchBar,
  }[calculatedVariant];

  return <Variant {...searchBarProps} />;
}

function useOptionalState<T>(
  value: T | undefined,
  existingUseState: [T, React.Dispatch<React.SetStateAction<T>>]
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const internalUseState = useState(value);

  const coalescedUseState = useMemo(
    () => (value === undefined ? existingUseState : internalUseState),
    [value, existingUseState, internalUseState]
  );

  return coalescedUseState;
}
